import Phaser from 'phaser'
import { BASE_PATH } from '../config/paths'
import { Logger } from '../utils/Logger'

type FrameInfo = { png: string, json: string, frames: number, preview?: string, suggestedFrameRate?: number }
type Manifest = { cells: Record<string, { bounce: FrameInfo; pop: FrameInfo }>, fx: Record<string, { heal_flash: FrameInfo; pop_burst: FrameInfo }> }

export default class Boot extends Phaser.Scene {
  constructor() { super('Boot') }
  preload() {
    this.load.image('default-bg', `${BASE_PATH}board/default-bg.png`)

    // Load appropriate background based on device aspect ratio
    const aspectRatio = window.innerWidth / window.innerHeight
    const isLandscape = aspectRatio > 1

    let bgSuffix = '1080x1920' // default portrait
    let bgWidth = 1080
    let bgHeight = 1920

    if (isLandscape) {
      if (aspectRatio > 2) {
        // iPhone X/11/12/13/14 landscape (very wide)
        bgSuffix = '2436x1125'
        bgWidth = 2436
        bgHeight = 1125
        Logger.getInstance().log('Boot: Loading iPhone X aspect backgrounds (2436x1125)')
      } else {
        // Standard landscape (16:9 or similar)
        bgSuffix = '1920x1080'
        bgWidth = 1920
        bgHeight = 1080
        Logger.getInstance().log('Boot: Loading standard landscape backgrounds (1920x1080)')
      }
    } else {
      Logger.getInstance().log('Boot: Loading portrait backgrounds (1080x1920)')
    }

    // Store background dimensions in registry for other scenes to use
    this.registry.set('bgWidth', bgWidth)
    this.registry.set('bgHeight', bgHeight)

    // Load all background options with the appropriate size
    this.load.image('bg', `${BASE_PATH}board/board_background_${bgSuffix}.png`)
    this.load.image('bg-pink', `${BASE_PATH}board/bg-pink-${bgSuffix}.png`)
    this.load.image('bg-blue', `${BASE_PATH}board/bg-blue-${bgSuffix}.png`)
    this.load.image('bg-rainbow', `${BASE_PATH}board/bg-rainbow-${bgSuffix}.png`)
    this.load.image('bg-purple', `${BASE_PATH}board/bg-purple-${bgSuffix}.png`)

    this.load.image('grid', `${BASE_PATH}board/grid_overlay_6x12_tile96.png`)
    this.load.json('sprites_manifest', `${BASE_PATH}sprites_manifest.json`)

    // Background music
    Logger.getInstance().log('Boot: Loading gameplay music from /music/byte-blast-8-bit-arcade-music-background-music-for-video-208780.mp3')
    this.load.audio('gameplay_music', `${BASE_PATH}music/byte-blast-8-bit-arcade-music-background-music-for-video-208780.mp3`)

    // Add audio loading event listeners
    this.load.on('filecomplete-audio-gameplay_music', () => {
      Logger.getInstance().log('Boot: Gameplay music loaded successfully!')
    })

    this.load.on('loaderror', (file: any) => {
      if (file.key === 'gameplay_music') {
        console.error('Boot: Failed to load gameplay music:', file)
      }
    })

    // UI Assets for GameHUD
    this.load.image('button_pause', `${BASE_PATH}ui/button_pause.png`)
    this.load.image('banner_purify', `${BASE_PATH}ui/banner_purify.png`)
  }
  create() {
    const m = this.cache.json.get('sprites_manifest') as Manifest

    // Map old cell names to new cell names for different game modes
    const cellTypeMapping: { [key: string]: string } = {
      // For Puyo modes, map old medical names to colors
      'rbc': 'red',
      'wbc': 'blue',
      'stem': 'green',
      'donor': 'yellow',
      'cancer': 'purple',
      'platelet': 'platelets'
    }

    Object.keys(m.cells).forEach(name => {
      const oldName = name.toLowerCase() // Convert "RBC" -> "rbc" etc.
      const newName = cellTypeMapping[oldName] || oldName

      // Load static idle images for UI elements
      this.load.image(`${newName}_idle`, `${BASE_PATH}cells/${oldName}_idle.png`)
      this.load.image(`${newName}_clear`, `${BASE_PATH}cells/${oldName}_clear.png`)

      // Load blink sprite sheet (8 frames at 128x128 each in a 1024x128 sheet)
      this.load.spritesheet(`${newName}_blink`, `${BASE_PATH}cells/${oldName}_blink.png`, {
        frameWidth: 128,
        frameHeight: 128
      })

      // Also load with OLD names for backward compatibility (puzzle mode uses medical names)
      this.load.image(`${oldName}_idle`, `${BASE_PATH}cells/${oldName}_idle.png`)
      this.load.image(`${oldName}_clear`, `${BASE_PATH}cells/${oldName}_clear.png`)
      this.load.spritesheet(`${oldName}_blink`, `${BASE_PATH}cells/${oldName}_blink.png`, {
        frameWidth: 128,
        frameHeight: 128
      })

      // Create fake bounce/pop textures by reusing idle - this prevents "texture not found" errors
      this.load.image(`${newName}_bounce`, `${BASE_PATH}cells/${oldName}_idle.png`)
      this.load.image(`${newName}_pop`, `${BASE_PATH}cells/${oldName}_idle.png`)
    })
    Object.keys(m.fx).forEach(name => {
      const oldName = name.toLowerCase()
      const newName = cellTypeMapping[oldName] || oldName

      // Skip FX atlases for now
      // this.load.atlas(`${newName}_heal_flash`, `/fx/${oldName}/${oldName}_heal_flash.png`, `/fx/${oldName}/${oldName}_heal_flash.json`)
      // this.load.atlas(`${newName}_pop_burst`, `/fx/${oldName}/${oldName}_pop_burst.png`, `/fx/${oldName}/${oldName}_pop_burst.json`)
    })
    this.load.once('complete', () => {
      // Debug: Log loaded textures
      Logger.getInstance().log('[Boot] Asset loading complete. Checking loaded textures:')
      const cellColors = ['red', 'blue', 'green', 'yellow', 'purple']
      const oldNames = ['rbc', 'wbc', 'stem', 'donor', 'cancer']

      cellColors.forEach((color, index) => {
        const oldName = oldNames[index]
        const hasNewTexture = this.textures.exists(`${color}_idle`)
        const hasOldTexture = this.textures.exists(`${oldName}_idle`)
        Logger.getInstance().log(`[Boot] ${color} (${oldName}): new=${hasNewTexture}, old=${hasOldTexture}`)
      })

      // Wait for Google Font to load before starting Title scene
      this.waitForFontAndStart()
    }); this.load.start()
  }

  private async waitForFontAndStart() {
    try {
      const startTime = performance.now();
      Logger.getInstance().log('[Boot] Waiting for document.fonts.ready...');
      await document.fonts.ready;
      const readyTime = performance.now();
      Logger.getInstance().log(`[Boot] document.fonts.ready resolved after ${(readyTime - startTime).toFixed(2)}ms`);

      // Use document.fonts.load with correct CSS font string for M PLUS Rounded 1c Black 900
      try {
        await document.fonts.load("normal 900 32px 'M PLUS Rounded 1c'");
        Logger.getInstance().log('[Boot] document.fonts.load called: normal 900 32px M PLUS Rounded 1c');
      } catch (e) {
        console.warn('[Boot] document.fonts.load failed, trying fallback:', e);
        try {
          await document.fonts.load("900 32px 'M PLUS Rounded 1c'");
          Logger.getInstance().log('[Boot] document.fonts.load fallback called: 900 32px M PLUS Rounded 1c');
        } catch (e2) {
          console.error('[Boot] document.fonts.load fallback also failed:', e2);
        }
      }

      // List all loaded fonts (debug)
      if (document.fonts) {
        let fontCount = 0;
        document.fonts.forEach(fontFace => {
          fontCount++;
          Logger.getInstance().log(`[Boot] FontFace #${fontCount}: family='${fontFace.family}', style='${fontFace.style}', weight='${fontFace.weight}', status='${fontFace.status}'`);
        });
        Logger.getInstance().log(`[Boot] document.fonts.forEach count: ${fontCount}`);
      }

      // Double-check the specific font is loaded
      const fontCheck = '900 32px "M PLUS Rounded 1c"';
      const fontLoaded = document.fonts.check(fontCheck);
      Logger.getInstance().log(`[Boot] document.fonts.check(${fontCheck}): ${fontLoaded}`);

      if (fontLoaded) {
        Logger.getInstance().log('[Boot] M PLUS Rounded 1c font loaded successfully');
      } else {
        console.warn('[Boot] M PLUS Rounded 1c font not detected, using fallback');
      }

      this.scene.start('Title');
    } catch (error) {
      console.warn('[Boot] Font loading error, proceeding anyway:', error);
      this.scene.start('Title');
    }
  }

  private createPuyoSprites() {
    // Define Puyo colors
    const puyoColors = {
      red: 0xff4444,
      blue: 0x4444ff,
      green: 0x44ff44,
      yellow: 0xffff44,
      purple: 0xff44ff,
      platelets: 0xffa500  // Orange
    }

    // Create colored blob sprites
    Object.entries(puyoColors).forEach(([colorName, colorValue]) => {
      // Create a circular Puyo-style blob
      const graphics = this.add.graphics()

      // Main blob circle
      graphics.fillStyle(colorValue, 1)
      graphics.fillCircle(48, 48, 40)

      // Highlight for 3D effect
      graphics.fillStyle(0xffffff, 0.3)
      graphics.fillCircle(40, 40, 15)

      // Dark outline
      graphics.lineStyle(3, 0x333333, 1)
      graphics.strokeCircle(48, 48, 40)

      // Generate texture for this color
      graphics.generateTexture(`${colorName}_idle`, 96, 96)
      graphics.generateTexture(`${colorName}_blink`, 96, 96)
      graphics.generateTexture(`${colorName}_clear`, 96, 96)

      // Also create bounce animations (just use same texture for now)
      graphics.generateTexture(`${colorName}_bounce`, 96, 96)
      graphics.generateTexture(`${colorName}_pop`, 96, 96)

      graphics.destroy()
    })
  }
}
